import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { onPrivateMessageReceived, sendPrivateMessage } from '../../Socket/socket';
import { useNotifications } from '../../Context/NotificationContext';
import { useMessages } from '../../Context/MessageContext';
import { useConfirm } from '../../Context/ConfirmContext';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';

// Import styled components
import {
  ContentContainer,
  ConversationViewContainer,
  HighlightStyle
} from './styled';

// Import components
import MessageInput from './components/MessageInput';
import SearchToolbar from './components/SearchToolbar';
import MessageList from './components/MessageList';
import PaymentDialog from './components/PaymentDialog';
import PaymentWarningDialog from './components/PaymentWarningDialog';
import SignatureRequestDialog from './components/SignatureRequestDialog';
import { InfoRequestComposer } from '../Dashboard Panels/CaseDashboard/ResumeBuilder/InfoRequests';
import { PaymentRequestForm } from '../PaymentRequest';

// Import utilities
import { findSearchMatches } from './utils/searchUtils';
import { createPaymentMessage } from './utils/fileUtils';
import { scrollToBottom, scrollToTop, scrollToMessage } from './utils/domUtils';

const ConversationView = ({ conversation, userId, setConversation }) => {
  // On a SHARED case (opened by a subcontractor because a contractor shared it
  // with them), payments/billing belong to the contractor — the subcontractor
  // only does the case work. Hide every payment action/dialog for shared cases.
  const isSharedCase = conversation?.shared === true;

  // Message state
  const [message, setMessage] = useState('');
  const [conversationL, setConversationL] = useState(conversation);
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Search state
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(-1);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchDebounceTimeout, setSearchDebounceTimeout] = useState(null);
  
  // Modal dialogs state
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentDescription, setPaymentDescription] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentLink, setPaymentLink] = useState('');
  const [showPaymentWarning, setShowPaymentWarning] = useState(false);
  const [pendingPaymentLink, setPendingPaymentLink] = useState('');
  const [showNewPaymentRequest, setShowNewPaymentRequest] = useState(false);

  // Flag message state
  const [flagModal, setFlagModal] = useState({ open: false, messageIndex: null, messageContent: '' });
  const [flagCorrection, setFlagCorrection] = useState('');
  const [flagSubmitting, setFlagSubmitting] = useState(false);

  // Add state to prevent multiple call links
  const [videoCallSent, setVideoCallSent] = useState(false);

  // Signature request state
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [showInfoRequest, setShowInfoRequest] = useState(false);
  const [isRequestingSignature, setIsRequestingSignature] = useState(false);

  // Refs
  const messagesEndRef = useRef(null);
  const searchInputRef = useRef(null);
  
  // Hooks
  const { user,getAccessTokenSilently } = useAuth0();
  const { addNotification } = useNotifications();
  const { addMessage } = useMessages();
  const { confirm } = useConfirm();
  const authenticatedAxios = useAuthenticatedAxios();

  const handleFlagMessage = (messageIndex, messageContent) => {
    setFlagModal({ open: true, messageIndex, messageContent });
    setFlagCorrection('');
  };

  // Delete a message from the feed (e.g. an active-control card sent by mistake).
  const handleDeleteMessage = async (messageIndex, type) => {
    const ok = await confirm({
      title: 'Delete message',
      message: 'This message will be permanently removed from the conversation. This cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger',
    });
    if (!ok) return;
    try {
      const threadId = conversation?.threadId;
      await authenticatedAxios.post(`/conversations/${threadId}/delete-message`, { index: messageIndex, type });
      setConversationL((prev) => ({
        ...prev,
        messages: (prev?.messages || []).filter((_, i) => i !== messageIndex)
      }));
      addNotification('Message deleted', 'success');
    } catch (err) {
      console.error('Error deleting message:', err);
      addNotification(err?.response?.data?.error || 'Failed to delete message', 'error');
    }
  };

  const submitFlagMoment = async () => {
    if (!flagCorrection.trim() || flagCorrection.trim().length < 5) return;
    setFlagSubmitting(true);
    try {
      const convoId = conversation?._id || conversation?.id;
      await authenticatedAxios.post(`/api/brain/conversations/${convoId}/flag-moment`, {
        messageIndex: flagModal.messageIndex,
        correction: flagCorrection.trim()
      });
      setFlagModal({ open: false, messageIndex: null, messageContent: '' });
      setFlagCorrection('');
      addNotification('Teaching created from flagged message', 'success');
    } catch (err) {
      console.error('Error flagging message:', err);
      addNotification('Failed to create teaching', 'error');
    } finally {
      setFlagSubmitting(false);
    }
  };

  /**
   * Handle search input change with debouncing
   */
  useEffect(() => {
    // Skip if empty search
    if (!searchText || searchText.trim() === '') {
      setSearchResults([]);
      setCurrentResultIndex(-1);
      return;
    }

    // Cancel any pending search operation
    if (searchDebounceTimeout) {
      clearTimeout(searchDebounceTimeout);
    }

    // Set new timeout for non-empty search term
    const newTimeout = setTimeout(() => {
      const results = findSearchMatches(conversationL.messages, searchText);
      setSearchResults(results);
      setCurrentResultIndex(results.length > 0 ? 0 : -1);

      // Focus first result if found
      if (results.length > 0) {
        setTimeout(() => {
          scrollToMessage(results[0], messagesEndRef);
        }, 100);
      }
    }, 300); // 300ms debounce

    setSearchDebounceTimeout(newTimeout);

    // Clean up timeout on unmount
    return () => {
      if (newTimeout) {
        clearTimeout(newTimeout);
      }
    };
  }, [searchText, conversationL?.messages]);

  /**
   * Listen for incoming private messages via socket
   */
  useEffect(() => {
    const handlePrivateMessage = (data) => {
      // Only filter by threadId for non-system messages
      if (data.type !== 'system' && data.threadId != conversation?.threadId) {
        return;
      }

      console.log('🔵 Socket Message Received:', {
        data,
        currentThreadId: conversation?.threadId,
        currentUserId: user.sub,
        isForCurrentUser: data.receiverId === user.sub,
        isFromOther: data.userId !== user.sub
      });

      const newMessage = {
        type: data.type,
        userId: data.userId,
        sender: data.username,
        // Author name stamped by the gateway — lets us show a subcontractor (not in
        // conversationUsers) as the real author, live.
        senderName: data.senderName || data.username,
        content: data.content,
        receiverId: data.receiverId,
        threadId: data.threadId, // Make sure threadId is included
        time: new Date().toISOString(),
        fileName: data.fileName,
        fileType: data.fileType,
        fileData: data.fileData,
        isHTML: data.isHTML
      };

      // Add message notification if it's for the current user and from a different conversation
      if (data.type !== 'system' && data.receiverId === user.sub && data.userId !== user.sub) {
        if (conversation?.threadId !== data.threadId) {
          addMessage({
            threadId: data.threadId,
            sender: data.username,
            content: data.content,
            timestamp: new Date().toISOString()
          });
        }
      }

      setConversationL((prev) => ({
        ...prev,
        messages: [...prev.messages, newMessage],
      }));
      scrollToBottom(messagesEndRef);
    };

    const unsubscribe = onPrivateMessageReceived(handlePrivateMessage);

    return () => {
      unsubscribe && unsubscribe();
    };
  }, [user.sub, conversation?.threadId, addMessage]);

  /**
   * Scroll to bottom when conversation changes
   */
  useEffect(() => {
    if (conversation?.messages) {
      scrollToBottom(messagesEndRef);
    }
  }, [conversation?.messages]);

  /**
   * Handle sending a message (text + optional file)
   */
  const handleSendMessage = () => {
    if (!message.trim() && !selectedFile) return;

    console.log('📤 Sending message:', {
      content: message,
      threadId: conversation?.threadId,
      receiverId: userId,
      senderId: user.sub
    });
    setLoadingMessage(true);

    const newMessage = {
      id: Date.now(), // Generate a unique ID, e.g., using UUID
      type: 'expert', // Set type based on whether a file is attached
      content: selectedFile ? `data:${selectedFile.type};base64,${selectedFile.data}` : message, // If file is attached, content can be empty or a description
      fileName: selectedFile ? selectedFile.name : '',
      fileType: selectedFile ? selectedFile.type : '',
      time: new Date().toISOString(),
      sender: user.sub, // Adjust based on your logic
    };
    
    // Send the message to the server
    if (conversation) {
      // Build payload for the server
      const payload = {
        content: message,
        threadId: conversation.threadId,
        receiverId: userId,
        senderId: user.sub,
        type: 'expert',
        time: new Date().toISOString(),
      };

      // If there's a selected file, add it to the payload
      if (selectedFile) {
        payload.fileName = selectedFile.name;
        payload.fileType = selectedFile.type;
        payload.fileData = selectedFile.data;
      }

      sendPrivateMessage(payload);

      // Update local conversation
      setConversationL((prev) => ({
        ...prev,
        messages: [...prev.messages, newMessage],
      }));
      scrollToBottom(messagesEndRef);
    }

    // Reset text input and selected file
    setMessage('');
    setSelectedFile(null);
    setLoadingMessage(false);
  };

  /**
   * Post a plain adviser message through the same socket path as the input box, so it shows
   * up live for both adviser and client (used after sending an information request).
   */
  const sendExpertText = (text) => {
    if (!conversation || !text) return;
    const time = new Date().toISOString();
    sendPrivateMessage({ content: text, threadId: conversation.threadId, receiverId: userId, senderId: user.sub, type: 'expert', time });
    setConversationL((prev) => ({
      ...prev,
      messages: [...(prev?.messages || []), { id: Date.now(), type: 'expert', content: text, fileName: '', fileType: '', time, sender: user.sub }],
    }));
    scrollToBottom(messagesEndRef);
  };

  /**
   * Navigation event handlers
   */
  const handleGoToPrevResult = () => {
    if (searchResults.length === 0) return;

    // Calculate the new index
    const newIndex = (currentResultIndex - 1 + searchResults.length) % searchResults.length;
    setCurrentResultIndex(newIndex);

    // Scroll to the message
    scrollToMessage(searchResults[newIndex], messagesEndRef);
  };

  const handleGoToNextResult = () => {
    if (searchResults.length === 0) return;

    // Calculate the new index
    const newIndex = (currentResultIndex + 1) % searchResults.length;
    setCurrentResultIndex(newIndex);

    // Scroll to the message
    scrollToMessage(searchResults[newIndex], messagesEndRef);
  };

  const handleScrollToTop = () => {
    scrollToTop(messagesEndRef);
  };

  const handleScrollToBottom = () => {
    scrollToBottom(messagesEndRef);
  };

  /**
   * Set up keyboard shortcuts
   */
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Ctrl+F or Command+F
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault(); // Prevent default browser search
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          setSearchFocused(true);
        }
      }

      // Handle Escape key to exit search
      if (e.key === 'Escape' && searchFocused) {
        setSearchFocused(false);
        setSearchText('');
        setSearchResults([]);
        setCurrentResultIndex(-1);
      }

      // Handle Enter key in search to go to next result
      if (e.key === 'Enter' && searchFocused && searchResults.length > 0) {
        e.preventDefault();
        handleGoToNextResult();
      }

      // F3 to go to next result, Shift+F3 to go to previous result
      if (e.key === 'F3') {
        e.preventDefault();
        if (searchResults.length > 0) {
          if (e.shiftKey) {
            handleGoToPrevResult();
          } else {
            handleGoToNextResult();
          }
        }
      }

      // Add shortcuts for Home/End to go to top/bottom
      if (e.key === 'Home' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleScrollToTop();
      }

      if (e.key === 'End' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleScrollToBottom();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [searchFocused, searchResults, currentResultIndex]);

  // Reset message content when new conversation is selected
  useEffect(() => {
    setSearchText('');
    setSearchResults([]);
    setCurrentResultIndex(-1);
    setConversationL(conversation);
  }, [conversation]);

  /**
   * Set up direct DOM event handlers as a reliable backup system
   */
  useEffect(() => {
    // Get the button elements
    const startButton = document.getElementById('toolbar-start-button');
    const endButton = document.getElementById('toolbar-end-button');
    const prevButton = document.getElementById('toolbar-prev-button');
    const nextButton = document.getElementById('toolbar-next-button');

    // Create direct event handlers
    const startButtonHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      scrollToTop(messagesEndRef);
      return false;
    };

    const endButtonHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      scrollToBottom(messagesEndRef);
      return false;
    };

    const prevButtonHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleGoToPrevResult();
      return false;
    };

    const nextButtonHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleGoToNextResult();
      return false;
    };

    // Attach handlers with both addEventListener and direct onclick property
    if (startButton) {
      startButton.addEventListener('click', startButtonHandler);
      startButton.onclick = startButtonHandler;
    }

    if (endButton) {
      endButton.addEventListener('click', endButtonHandler);
      endButton.onclick = endButtonHandler;
    }

    if (prevButton) {
      prevButton.addEventListener('click', prevButtonHandler);
      prevButton.onclick = prevButtonHandler;
    }

    if (nextButton) {
      nextButton.addEventListener('click', nextButtonHandler);
      nextButton.onclick = nextButtonHandler;
    }

    // Comprehensive cleanup to prevent memory leaks
    return () => {
      if (startButton) {
        startButton.removeEventListener('click', startButtonHandler);
        startButton.onclick = null;
      }
      if (endButton) {
        endButton.removeEventListener('click', endButtonHandler);
        endButton.onclick = null;
      }
      if (prevButton) {
        prevButton.removeEventListener('click', prevButtonHandler);
        prevButton.onclick = null;
      }
      if (nextButton) {
        nextButton.removeEventListener('click', nextButtonHandler);
        nextButton.onclick = null;
      }
    };
  }, [messagesEndRef.current, searchResults, currentResultIndex]);
  
  /**
   * Handle payment link click
   */
  const handlePaymentLinkClick = (link) => {
    setPendingPaymentLink(link);
    setShowPaymentWarning(true);
  };

  /**
   * Handle payment dialog submission with Rapyd integration
   */
  const handleRapydPaymentSubmit = async () => {
    // Check if Rapyd is enabled
    if (process.env.REACT_APP_ENABLE_RAPYD !== 'true') {
      addNotification('Rapyd integration is not enabled', 'error');
      return;
    }

    if (!conversation || !paymentAmount || !paymentDescription) {
      addNotification('Please fill in all required fields', 'error');
      return;
    }

    setLoadingMessage(true);

    try {
      const token = await getAccessTokenSilently();
      // Create payment request via API
      const response = await fetch(`${process.env.REACT_APP_SERVER}payment/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          conversationId: conversation.threadId,
          expertId: user.sub,
          clientId: userId,
          amount: parseFloat(paymentAmount),
          currency: 'USD',
          description: paymentDescription
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create payment request');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to create payment request');
      }

      const { checkoutUrl, paymentRequest, fallbackMode, expertWallet } = result.data;

      let paymentMessageHtml;

      if (fallbackMode) {
        // Fallback mode - show payment request with wallet info
        paymentMessageHtml = `
          <div style="border: 2px solid #ff9800; border-radius: 12px; padding: 16px; margin: 8px 0; background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%); font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
            <div style="display: flex; align-items: center; margin-bottom: 12px;">
              <div style="background: #ff9800; color: white; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; font-size: 18px; margin-right: 12px;">💳</div>
              <div>
                <div style="font-weight: 600; color: #ef6c00; font-size: 16px;">Payment Request</div>
                <div style="color: #666; font-size: 13px;">Rapyd wallet ready • Manual payment</div>
              </div>
            </div>
            <div style="margin-bottom: 12px;">
              <div style="font-weight: 500; color: #333; margin-bottom: 4px;">Amount: <span style="color: #2e7d32; font-weight: 600;">$${paymentAmount}</span></div>
              <div style="color: #666; font-size: 14px; line-height: 1.4; margin-bottom: 8px;">${paymentDescription}</div>
              <div style="background: #f5f5f5; padding: 8px; border-radius: 4px; font-size: 12px; color: #666;">
                <strong>Rapyd Wallet ID:</strong> ${expertWallet?.walletId || 'Available'}
              </div>
            </div>
            <div style="text-align: center; margin-top: 16px;">
              <div style="color: #666; font-size: 13px;">
                💰 Expert has Rapyd wallet configured<br/>
                📧 Contact expert for payment details
              </div>
            </div>
          </div>
        `;
      } else {
        // Normal mode - Rapyd checkout link
        paymentMessageHtml = `
          <div style="border: 2px solid #e3f2fd; border-radius: 12px; padding: 16px; margin: 8px 0; background: linear-gradient(135deg, #f8f9ff 0%, #e8f2ff 100%); font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
            <div style="display: flex; align-items: center; margin-bottom: 12px;">
              <div style="background: #2196f3; color: white; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; font-size: 18px; margin-right: 12px;">💳</div>
              <div>
                <div style="font-weight: 600; color: #1976d2; font-size: 16px;">Payment Request</div>
                <div style="color: #666; font-size: 13px;">Secure payment via Rapyd</div>
              </div>
            </div>
            <div style="margin-bottom: 12px;">
              <div style="font-weight: 500; color: #333; margin-bottom: 4px;">Amount: <span style="color: #2e7d32; font-weight: 600;">$${paymentAmount}</span></div>
              <div style="color: #666; font-size: 14px; line-height: 1.4;">${paymentDescription}</div>
            </div>
            <div style="text-align: center; margin-top: 16px;">
              <a href="${checkoutUrl}" target="_blank" rel="noopener noreferrer" class="payment-link" data-payment-link="${checkoutUrl}" style="display: inline-block; background: linear-gradient(135deg, #4caf50 0%, #45a049 100%); color: white; text-decoration: none; padding: 12px 24px; border-radius: 25px; font-weight: 600; font-size: 14px; box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3); transition: all 0.3s ease; border: none;">
                🔒 Pay Securely
              </a>
            </div>
            <div style="text-align: center; margin-top: 8px; color: #999; font-size: 11px;">
              Powered by Rapyd • SSL Encrypted
            </div>
          </div>
        `;
      }

      const payload = {
        content: paymentMessageHtml,
        threadId: conversation.threadId,
        receiverId: userId,
        senderId: user.sub,
        type: 'expert',
        time: new Date().toISOString(),
        isHTML: true,
        metadata: {
          paymentRequestId: paymentRequest._id,
          checkoutId: paymentRequest.rapydCheckoutId
        }
      };

      sendPrivateMessage(payload);

      // Update local conversation
      const newMessage = {
        id: Date.now(),
        type: 'expert',
        content: paymentMessageHtml,
        time: new Date().toISOString(),
        sender: user.sub,
        isHTML: true,
      };

      setConversationL((prev) => ({
        ...prev,
        messages: [...prev.messages, newMessage],
      }));

      scrollToBottom(messagesEndRef);

      // Set up event listener for payment links after message is added
      setTimeout(() => {
        const paymentLinks = document.querySelectorAll('.payment-link');
        paymentLinks.forEach(link => {
          if (!link.hasAttribute('data-listener-added')) {
            link.setAttribute('data-listener-added', 'true');
            link.addEventListener('click', (e) => {
              e.preventDefault();
              const paymentUrl = link.getAttribute('data-payment-link');
              handlePaymentLinkClick(paymentUrl);
            });
          }
        });
      }, 100);

      addNotification('Payment request sent successfully', 'success');

    } catch (error) {
      console.error('Error creating payment request:', error);
      addNotification(error.message || 'Failed to create payment request', 'error');
    } finally {
      setLoadingMessage(false);
    }

    // Close the dialog and reset fields
    setShowPaymentDialog(false);
    setPaymentDescription('');
    setPaymentAmount('');
    setPaymentLink('');
  };

  /**
   * Handle manual payment dialog submission (original functionality)
   */
  const handlePaymentSubmit = () => {
    // Send the payment request as a message in the conversation
    if (conversation) {
      const paymentMessageHtml = createPaymentMessage(paymentAmount, paymentDescription, paymentLink);

      const payload = {
        content: paymentMessageHtml,
        threadId: conversation.threadId,
        receiverId: userId,
        senderId: user.sub,
        type: 'expert',
        time: new Date().toISOString(),
        isHTML: true, // Flag to indicate HTML content
      };

      sendPrivateMessage(payload);

      // Update local conversation
      const newMessage = {
        id: Date.now(),
        type: 'expert',
        content: paymentMessageHtml,
        time: new Date().toISOString(),
        sender: user.sub,
        isHTML: true, // Flag to indicate HTML content
      };

      setConversationL((prev) => ({
        ...prev,
        messages: [...prev.messages, newMessage],
      }));

      scrollToBottom(messagesEndRef);

      // Set up event listener for payment links after message is added
      setTimeout(() => {
        const paymentLinks = document.querySelectorAll('.payment-link');
        paymentLinks.forEach(link => {
          if (!link.hasAttribute('data-listener-added')) {
            link.setAttribute('data-listener-added', 'true');
            link.addEventListener('click', (e) => {
              e.preventDefault();
              const paymentUrl = link.getAttribute('data-payment-link');
              handlePaymentLinkClick(paymentUrl);
            });
          }
        });
      }, 100);
    }

    // Close the dialog and reset fields
    setShowPaymentDialog(false);
    setPaymentDescription('');
    setPaymentAmount('');
    setPaymentLink('');
  };

  // Handler to send video call link
  const handleSendVideoCallLink = () => {
    if (!conversation?.threadId || !user?.sub) return;
    const videoCallUrl = `/video-call/${user.sub}/${conversation.threadId}`;
    const videoCallMessageHtml = `<a href="${videoCallUrl}" target="_blank" rel="noopener noreferrer" style="color:#007bff;font-weight:bold;">Join Video Call</a>`;

    const payload = {
      content: videoCallUrl,
      threadId: conversation.threadId,
      receiverId: userId,
      senderId: user.sub,
      type: 'video-call',
      time: new Date().toISOString(),
      isHTML: true,
    };

    sendPrivateMessage(payload);
    // Add to local conversation
    const newMessage = {
      id: Date.now(),
      type: 'video-call',
      content: videoCallUrl,
      time: new Date().toISOString(),
      sender: user.sub,
      isHTML: true,
    };
    
    setConversationL((prev) => ({
      ...prev,
      messages: [...prev.messages, newMessage],
    }));
    setVideoCallSent(true);
    scrollToBottom(messagesEndRef);
  };

  // Handler to create and share whiteboard link
  const handleStartWhiteboard = async () => {
    if (!conversation?.threadId || !user?.sub) return;

    try {
      // Create whiteboard on server
      const response = await authenticatedAxios.post('/api/whiteboard', {
        threadId: conversation.threadId,
        title: `Whiteboard - ${new Date().toLocaleString()}`
      });

      const { roomId, shareLink } = response.data.whiteboard;
      const fullWhiteboardUrl = `${window.location.origin}${shareLink}`;

      // Send whiteboard link as a message
      const payload = {
        content: shareLink,
        threadId: conversation.threadId,
        receiverId: userId,
        senderId: user.sub,
        type: 'whiteboard',
        time: new Date().toISOString(),
        isHTML: true,
      };

      sendPrivateMessage(payload);

      // Add to local conversation
      const newMessage = {
        id: Date.now(),
        type: 'whiteboard',
        content: shareLink,
        time: new Date().toISOString(),
        sender: user.sub,
        isHTML: true,
      };

      setConversationL((prev) => ({
        ...prev,
        messages: [...prev.messages, newMessage],
      }));

      scrollToBottom(messagesEndRef);

      // Open whiteboard in new tab for the expert
      window.open(fullWhiteboardUrl, '_blank', 'noopener,noreferrer');

    } catch (error) {
      console.error('Error creating whiteboard:', error);
      addNotification({
        type: 'error',
        message: 'Failed to create whiteboard. Please try again.'
      });
    }
  };

  // Handler to request signature from client
  const handleSignatureRequest = async ({ purpose, description }) => {
    if (!conversation?.threadId || !user?.sub) return;

    setIsRequestingSignature(true);

    try {
      // Create signature request on server
      const response = await authenticatedAxios.post('/signatures/request', {
        conversationId: conversation.threadId,
        purpose,
        description,
        odId: userId,
        userName: conversation?.users?.find(u => u.id === userId)?.name || 'Client'
      });

      const { requestCode, expiresAt } = response.data.data;

      // Create message content as JSON
      const messageContent = JSON.stringify({
        requestCode,
        purpose,
        description,
        status: 'pending',
        expiresAt
      });

      // Send signature request as a message
      const payload = {
        content: messageContent,
        threadId: conversation.threadId,
        receiverId: userId,
        senderId: user.sub,
        type: 'signature_request',
        time: new Date().toISOString(),
        isHTML: false,
      };

      sendPrivateMessage(payload);

      // Add to local conversation
      const newMessage = {
        id: Date.now(),
        type: 'signature_request',
        content: messageContent,
        time: new Date().toISOString(),
        sender: user.sub,
        isHTML: false,
      };

      setConversationL((prev) => ({
        ...prev,
        messages: [...prev.messages, newMessage],
      }));

      setShowSignatureDialog(false);
      scrollToBottom(messagesEndRef);

      addNotification({
        type: 'success',
        message: 'Signature request sent successfully!'
      });

    } catch (error) {
      console.error('Error creating signature request:', error);
      addNotification({
        type: 'error',
        message: 'Failed to send signature request. Please try again.'
      });
    } finally {
      setIsRequestingSignature(false);
    }
  };

  // Handler to send service questionnaire
  const handleSendServiceQuestionnaire = async (serviceType) => {
    if (!conversation?.threadId || !user?.sub || !userId) return;

    try {
      // Get conversation ID (MongoDB _id)
      const conversationId = conversation._id || conversation.id;

      // Create service questionnaire session on server
      const response = await authenticatedAxios.post('/service-questionnaire/sessions', {
        serviceType,
        conversationId,
        threadId: conversation.threadId,
        userId: userId,
        userName: conversation?.users?.find(u => u.id === userId)?.name,
        userEmail: conversation?.users?.find(u => u.id === userId)?.email
      });

      const { session, sessionUrl } = response.data;

      // Create message content as JSON
      const messageContent = JSON.stringify({
        sessionId: session.sessionId,
        serviceType: session.serviceType,
        serviceName: session.serviceName,
        serviceIcon: session.serviceIcon,
        sessionUrl
      });

      // Send service questionnaire as a message
      const payload = {
        content: messageContent,
        threadId: conversation.threadId,
        receiverId: userId,
        senderId: user.sub,
        type: `service_questionnaire_${serviceType}`,
        time: new Date().toISOString(),
        isHTML: false,
      };

      sendPrivateMessage(payload);

      // Add to local conversation
      const newMessage = {
        id: Date.now(),
        type: `service_questionnaire_${serviceType}`,
        content: messageContent,
        time: new Date().toISOString(),
        sender: user.sub,
        isHTML: false,
      };

      setConversationL((prev) => ({
        ...prev,
        messages: [...prev.messages, newMessage],
      }));

      scrollToBottom(messagesEndRef);

      addNotification({
        type: 'success',
        message: `${session.serviceName} questionnaire sent to client!`
      });

    } catch (error) {
      console.error('Error creating service questionnaire:', error);
      addNotification({
        type: 'error',
        message: 'Failed to send questionnaire. Please try again.'
      });
    }
  };

  return (
    <ContentContainer>
      <ConversationViewContainer>
        <HighlightStyle />
        {/* Toolbar with search functionality */}
        <SearchToolbar
          ref={searchInputRef}
          searchText={searchText}
          setSearchText={setSearchText}
          searchResults={searchResults}
          currentResultIndex={currentResultIndex}
          handleGoToPrevResult={handleGoToPrevResult}
          handleGoToNextResult={handleGoToNextResult}
          handleScrollToTop={handleScrollToTop}
          handleScrollToBottom={handleScrollToBottom}
          setShowPaymentDialog={setShowPaymentDialog}
          setShowNewPaymentRequest={setShowNewPaymentRequest}
          isSharedCase={isSharedCase}
          onStartVideoCall={handleSendVideoCallLink}
          onStartWhiteboard={handleStartWhiteboard}
          onRequestSignature={() => setShowSignatureDialog(true)}
          onRequestInfo={() => setShowInfoRequest(true)}
          onSendServiceQuestionnaire={handleSendServiceQuestionnaire}
        />

        {showInfoRequest && (
          <InfoRequestComposer
            threadId={conversation?._id || conversation?.threadId}
            defaultEmail={conversation?.users?.find(u => u.id === userId)?.email || ''}
            defaultName={conversation?.users?.find(u => u.type === 'user')?.name || ''}
            postToConversation={false}
            onClose={() => setShowInfoRequest(false)}
            onSent={(req) => sendExpertText(
              req.language === 'fr'
                ? `Je vous ai envoyé quelques questions par courriel. Vous pouvez aussi y répondre ici : ${req.url}`
                : `I've emailed you a few questions. You can also answer them here: ${req.url}`
            )}
          />
        )}

        {/* Message list */}
                  <MessageList
            ref={messagesEndRef}
            messages={conversationL?.messages}
            searchText={searchText}
            searchResults={searchResults}
            currentResultIndex={currentResultIndex}
            currentUser={user}
            conversationUsers={conversation?.users}
            conversationId={conversation?._id || conversation?.id}
            onFlagMessage={handleFlagMessage}
            onDeleteMessage={handleDeleteMessage}
          />

        {/* Message input */}
        <MessageInput
          message={message}
          setMessage={setMessage}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          handleSendMessage={handleSendMessage}
          loadingMessage={loadingMessage}
        />

        {/* Modals — payment dialogs are never rendered for shared (subcontracted) cases */}
        {!isSharedCase && (
        <>
        <PaymentDialog
          showPaymentDialog={showPaymentDialog}
          setShowPaymentDialog={setShowPaymentDialog}
          paymentDescription={paymentDescription}
          setPaymentDescription={setPaymentDescription}
          paymentAmount={paymentAmount}
          setPaymentAmount={setPaymentAmount}
          paymentLink={paymentLink}
          setPaymentLink={setPaymentLink}
          handlePaymentSubmit={handlePaymentSubmit}
          handleRapydPaymentSubmit={handleRapydPaymentSubmit}
        />

        <PaymentWarningDialog
          showPaymentWarning={showPaymentWarning}
          setShowPaymentWarning={setShowPaymentWarning}
          pendingPaymentLink={pendingPaymentLink}
        />

        {/* New Payment Request Form (uses expert's payment settings) */}
        <PaymentRequestForm
          isOpen={showNewPaymentRequest}
          onClose={() => setShowNewPaymentRequest(false)}
          conversationId={conversation?._id}
          clientInfo={{
            name: conversation?.users?.find(u => u.type === 'user')?.name || '',
            email: '' // Will need to be filled in by expert
          }}
          onPaymentCreated={(paymentRequest) => {
            // Add payment request to conversation as a message
            const paymentMessage = {
              id: Date.now(),
              type: 'payment_request',
              content: JSON.stringify(paymentRequest),
              time: new Date().toISOString(),
              userId: user.sub
            };
            setConversationL((prev) => ({
              ...prev,
              messages: [...prev.messages, paymentMessage]
            }));
            scrollToBottom(messagesEndRef);
          }}
        />
        </>
        )}

        {/* Signature Request Dialog */}
        <SignatureRequestDialog
          showDialog={showSignatureDialog}
          setShowDialog={setShowSignatureDialog}
          onSubmit={handleSignatureRequest}
          isLoading={isRequestingSignature}
        />

        {/* Flag Message Modal */}
        {flagModal.open && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 9999
          }} onClick={() => setFlagModal({ open: false, messageIndex: null, messageContent: '' })}>
            <div style={{
              background: '#1e1e2e', borderRadius: 12, padding: 24, width: 520, maxWidth: '90vw',
              maxHeight: '80vh', overflow: 'auto', border: '1px solid rgba(255,255,255,0.1)'
            }} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 16px', color: '#fff', fontSize: 16 }}>Flag AI Response & Create Teaching</h3>
              <div style={{
                background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: 12,
                marginBottom: 16, maxHeight: 150, overflow: 'auto',
                fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5
              }}>
                {(flagModal.messageContent || '').slice(0, 500)}
                {(flagModal.messageContent || '').length > 500 && '...'}
              </div>
              <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, marginBottom: 6, display: 'block' }}>
                What should the AI have said instead?
              </label>
              <textarea
                value={flagCorrection}
                onChange={e => setFlagCorrection(e.target.value)}
                placeholder="Describe the correction or better response..."
                style={{
                  width: '100%', minHeight: 100, background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8,
                  color: '#fff', padding: 10, fontSize: 14, resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                <button
                  onClick={() => setFlagModal({ open: false, messageIndex: null, messageContent: '' })}
                  style={{
                    background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8,
                    color: '#fff', padding: '8px 16px', cursor: 'pointer', fontSize: 13
                  }}
                >Cancel</button>
                <button
                  onClick={submitFlagMoment}
                  disabled={flagSubmitting || flagCorrection.trim().length < 5}
                  style={{
                    background: flagCorrection.trim().length >= 5 ? '#6366f1' : 'rgba(99,102,241,0.3)',
                    border: 'none', borderRadius: 8, color: '#fff',
                    padding: '8px 16px', cursor: flagCorrection.trim().length >= 5 ? 'pointer' : 'default',
                    fontSize: 13, opacity: flagSubmitting ? 0.6 : 1
                  }}
                >{flagSubmitting ? 'Creating...' : 'Create Teaching'}</button>
              </div>
            </div>
          </div>
        )}
      </ConversationViewContainer>
    </ContentContainer>
  );
};

export default ConversationView; 